import * as React from 'react';
import { useState, useEffect } from 'react';

import { Input } from '@/components/TailwindComponents/base/Input';

interface DebouncedInputProps extends React.ComponentProps<'input'> {
  debounceTime?: number;
  value?: string;
}

const DebouncedInput = React.forwardRef<HTMLInputElement, DebouncedInputProps>(
  (
    {
      className,
      type,
      onChange,
      value: initialValue = '',
      debounceTime = 300,
      ...props
    },
    ref,
  ) => {
    const [value, setValue] = useState(initialValue);

    // Update internal value when external value changes
    useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
      const handler = setTimeout(() => {
        if (onChange) {
          const syntheticEvent = {
            target: { value },
            currentTarget: { value },
            preventDefault: () => {},
            stopPropagation: () => {},
          } as React.ChangeEvent<HTMLInputElement>;

          onChange(syntheticEvent);
        }
      }, debounceTime);

      return () => {
        clearTimeout(handler);
      };
    }, [value, debounceTime, onChange]);

    return (
      <Input
        type={type}
        className={className}
        ref={ref}
        value={value}
        {...props}
        onChange={(e) => setValue(e.target.value)}
      />
    );
  },
);

DebouncedInput.displayName = 'DebouncedInput';

export { DebouncedInput };
